import { memo, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/system';
import { SingularStationArea } from '@thive/fddm-types';

import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useHasOmniVehicles } from '@/modules/vehicles/hooks/useHasOmniVehicles';
import { CrabSettingInput } from '../common/CrabSettingInput';
import { getConnectionsWithSmoothHeading } from '../common/crabSettingConnections';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';

type Props = {
  selectedSingularStationAreas: SingularStationArea[];
};

function SingularStationPoiPreferencesComponent({ selectedSingularStationAreas }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);
  const { confirm, Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const hasOmniVehicles = useHasOmniVehicles();

  // null (no option selected) when the stations have different crab settings
  const crabSetting = useMemo(
    () =>
      getCommonNumber(
        selectedSingularStationAreas.map(
          ({ loadHandlingPoiSpecs }) => loadHandlingPoiSpecs[0]?.crabSetting ?? 0,
        ),
      ),
    [selectedSingularStationAreas],
  );

  const handleCrabChange = useCallback(
    async (value: number) => {
      const smoothHeading = value !== 0 ? true : fddmSvc.getPreference('connection.smoothHeading');
      const connectionsToUpdate = getConnectionsWithSmoothHeading(
        useFloorplanDesignStore
          .getState()
          .interAreaConnectionActions.getRelatedTo(
            selectedSingularStationAreas.map(({ id }) => id),
          ),
        smoothHeading,
      );

      // The connections aren't visible here, so ask before changing them
      if (connectionsToUpdate.length > 0) {
        const confirmed = await confirm(
          t('interface:properties.area.crab_setting_confirm.label'),
          t('interface:properties.area.crab_setting_confirm.message', {
            count: connectionsToUpdate.length,
          }),
        );
        if (!confirmed) return;
      }

      const updatedAreas = createEmptyAreas();
      selectedSingularStationAreas.forEach((area) =>
        pushAreaToItsCollection(updatedAreas, {
          ...area,
          loadHandlingPoiSpecs: area.loadHandlingPoiSpecs.map((spec) => ({
            ...spec,
            crabSetting: value,
          })),
        }),
      );

      historySvc.batch(() => {
        fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.crabSetting', value);
        fddmSvc.updateAreas(updatedAreas);
        if (connectionsToUpdate.length > 0) {
          fddmSvc.updateInterAreaConnections(connectionsToUpdate);
        }
      });
    },
    [confirm, fddmSvc, historySvc, selectedSingularStationAreas, t],
  );

  if (!hasOmniVehicles) return null;

  return (
    <Stack spacing={1}>
      <Dialog anchor={ref} offset={[0, 100]} />
      <div ref={ref}>
        <PropertiesLabel i18nkey='interface:properties.area.crab_setting'>
          <CrabSettingInput value={crabSetting} onChange={handleCrabChange} />
        </PropertiesLabel>
      </div>
    </Stack>
  );
}

export const SingularStationPoiPreferences = memo(SingularStationPoiPreferencesComponent);
